<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import ExportDataButton from '@/components/ExportDataButton.vue';
import PageState from '@/components/PageState.vue';
import StatusChip from '@/components/StatusChip.vue';
import type { PlanTask, Project, ProjectPlanResponse } from '@/types/domain';
import { buildExportFilename } from '@/utils/csv-export';
import { planExportColumns, planTaskExportRow } from '@/utils/data-export';
import { errorMessage, formatDate, formatResponsibility } from '@/utils/format';

type Scope = 'mine' | 'related' | 'all';
type StatusFilter = 'open' | 'all' | 'overdue' | 'blocked' | 'completed' | 'unscheduled';

interface BoardChild {
  task: PlanTask;
  /** 缩进层级，1 = 顶层行的直接子任务 */
  depth: number;
}

interface BoardRow {
  task: PlanTask;
  children: BoardChild[];
  /** 父行自身没命中筛选，是因为子行才出现的 */
  matchedByChild: boolean;
}

interface StageColumn {
  key: string;
  code: string;
  name: string;
  rows: BoardRow[];
  /** 该阶段顶层行数（不随筛选变化，与旧 EQS 口径一致） */
  total: number;
  done: number;
  percent: number;
}

const props = defineProps<{ project: Project }>();
const data = ref<ProjectPlanResponse | null>(null);
const loading = ref(true);
const error = ref('');
const scope = ref<Scope>('mine');
/** 因为本人任务为 0 而自动放宽了视角，需要在界面上说一声 */
const autoWidened = ref(false);
const statusFilter = ref<StatusFilter>('open');
const query = ref('');

const terminalStatuses = new Set(['completed', 'approved', 'cancelled', 'not_applicable']);

/**
 * 一个节点算不算「做完了」。
 *
 * 关键在 actual_at：旧 EQS 就是用「实际完成日期」判定完成的，
 * 迁移过来时 status_key 一个都没写——全库 70 个节点有实际完成日期，
 * 其中 65 个状态是空的，status_key='completed' 的是 0 个。
 * 只认 status 的话，每个阶段都显示 0%，看板像是从没人干过活。
 *
 * 填了实际完成日期就是做完了，这不是推测，是这个字段的含义。
 */
function isDone(task: PlanTask): boolean {
  return terminalStatuses.has(task.status) || Boolean(task.actualEnd);
}

/** 实际完成晚于计划完成 = 超期完成，旧看板用橙色单独标出来 */
function isLateDone(task: PlanTask): boolean {
  return Boolean(task.actualEnd && task.planEnd && task.actualEnd.slice(0, 10) > task.planEnd.slice(0, 10));
}
const allNodes = computed(() => data.value?.tasks ?? []);
const activityMap = computed(() => new Map(
  allNodes.value
    .filter((task) => task.activityId !== null && task.activityId !== undefined)
    .map((task) => [String(task.activityId), task]),
));
const phaseRoots = computed(() => allNodes.value.filter((task) =>
  (task.nodeType === 'phase' || /phase|stage|阶段/i.test(task.phaseCode))
  && (task.parentId === null || String(task.parentId) === '0')));
const taskPool = computed(() => allNodes.value.filter((task) => task.nodeType !== 'phase'));

function taskKey(task: PlanTask): string {
  return String(task.activityId ?? task.id);
}

function stageKeyFor(task: PlanTask): string | null {
  const seen = new Set<string>();
  let current: PlanTask | undefined = task;
  while (current) {
    if (
      (current.nodeType === 'phase' || /phase|stage|阶段/i.test(current.phaseCode))
      && (current.parentId === null || String(current.parentId) === '0')
    ) {
      return taskKey(current);
    }
    const parent = current.parentId === null ? '' : String(current.parentId);
    if (!parent || parent === '0' || seen.has(parent)) break;
    seen.add(parent);
    current = activityMap.value.get(parent);
  }
  return null;
}

function scopeMatches(task: PlanTask): boolean {
  if (scope.value === 'mine') return task.isMine === true;
  if (scope.value === 'related') return task.isRelated === true;
  return true;
}

function statusMatches(task: PlanTask): boolean {
  if (statusFilter.value === 'all') return true;
  if (statusFilter.value === 'overdue') return task.overdue;
  if (statusFilter.value === 'blocked') return task.status === 'blocked';
  if (statusFilter.value === 'completed') return isDone(task);
  if (statusFilter.value === 'unscheduled') return !task.planStart && !task.planEnd;
  return !isDone(task);
}

const scopedTasks = computed(() => taskPool.value.filter(scopeMatches));
const filteredTasks = computed(() => {
  const keyword = query.value.trim().toLocaleLowerCase();
  return scopedTasks.value.filter((task) =>
    statusMatches(task)
    && (!keyword || [
      task.name,
      task.ownerName,
      task.ownerRole,
      ...(task.outputs ?? []),
      ...(task.reviewRoles ?? []),
    ].filter(Boolean).join(' ').toLocaleLowerCase().includes(keyword)));
});

function stageCode(phase: PlanTask, index: number): string {
  return phase.name.match(/\bP0?[1-6]\b/i)?.[0].toUpperCase().replace(/^P([1-6])$/, 'P0$1')
    ?? `P0${index + 1}`;
}

/** 直属父节点的 key；父是阶段根或找不到父时返回 null（= 本身就是顶层行） */
function parentKeyOf(task: PlanTask): string | null {
  const parent = task.parentId === null || task.parentId === undefined ? '' : String(task.parentId);
  if (!parent || parent === '0') return null;
  const node = activityMap.value.get(parent);
  if (!node) return null;
  const isPhaseRoot = (node.nodeType === 'phase' || /phase|stage|阶段/i.test(node.phaseCode))
    && (node.parentId === null || String(node.parentId) === '0');
  return isPhaseRoot ? null : parent;
}

/** 某个顶层行下面的全部后代，按层级展开并带上缩进深度 */
function descendantsOf(rootKey: string, pool: readonly PlanTask[]): BoardChild[] {
  const byParent = new Map<string, PlanTask[]>();
  for (const task of pool) {
    const parent = parentKeyOf(task);
    if (parent) byParent.set(parent, [...(byParent.get(parent) ?? []), task]);
  }
  const out: BoardChild[] = [];
  const walk = (key: string, depth: number, seen: Set<string>): void => {
    if (seen.has(key)) return;
    seen.add(key);
    for (const child of byParent.get(key) ?? []) {
      out.push({ task: child, depth });
      walk(taskKey(child), depth + 1, seen);
    }
  };
  walk(rootKey, 1, new Set());
  return out;
}

const columns = computed<StageColumn[]>(() => {
  const groups = new Map<string, PlanTask[]>();
  for (const task of filteredTasks.value) {
    const key = stageKeyFor(task) ?? 'unassigned-stage';
    groups.set(key, [...(groups.get(key) ?? []), task]);
  }

  /**
   * 进度口径：数该阶段「全部」任务，含折叠起来的子任务。
   *
   * 折叠只是显示手段，不改计数——X254 的 P03/P04/P05/P06 用拍平口径与旧 EQS
   * 完全吻合（67 / 56 / 10 / 17），只数顶层行会变成 8 / 6 / 8 / 14，明显不对。
   * 分母同样不跟筛选走：跟着变的话切一次视角百分比就变，两次看到的数字没法比。
   */
  const progressFor = (key: string) => {
    const all = taskPool.value.filter((task) => (stageKeyFor(task) ?? 'unassigned-stage') === key);
    const done = all.filter(isDone).length;
    return { total: all.length, done, percent: all.length ? Math.round((done / all.length) * 100) : 0 };
  };

  /**
   * 组装成「顶层行 + 折叠的后代」。
   *
   * 父行本身被筛掉、但子行命中时仍然保留父行——否则命中的子任务会凭空消失。
   * 这种父行标记为 matchedByChild，界面上淡一点，说明它是因为子项才出现的。
   */
  const rowsFor = (key: string): BoardRow[] => {
    const visible = groups.get(key) ?? [];
    const visibleKeys = new Set(visible.map(taskKey));
    const stagePool = taskPool.value.filter(
      (task) => (stageKeyFor(task) ?? 'unassigned-stage') === key,
    );
    const rows: BoardRow[] = [];
    for (const task of stagePool) {
      if (parentKeyOf(task) !== null) continue;
      const children = descendantsOf(taskKey(task), stagePool)
        .filter((item) => visibleKeys.has(taskKey(item.task)));
      const selfVisible = visibleKeys.has(taskKey(task));
      if (!selfVisible && !children.length) continue;
      rows.push({ task, children, matchedByChild: !selfVisible });
    }
    return rows;
  };

  const stages = phaseRoots.value.map((phase, index) => ({
    key: taskKey(phase),
    code: stageCode(phase, index),
    name: phase.name.replace(/\bP0?[1-6]\b[\s·:：-]*/i, '') || phase.name,
    rows: rowsFor(taskKey(phase)),
    ...progressFor(taskKey(phase)),
  }));
  const unassigned = groups.get('unassigned-stage') ?? [];
  return unassigned.length
    ? [...stages, {
      key: 'unassigned-stage', code: '待归类', name: '未归入阶段',
      rows: rowsFor('unassigned-stage'), ...progressFor('unassigned-stage'),
    }]
    : stages;
});

const expanded = ref<Set<string>>(new Set());

function toggleRow(task: PlanTask): void {
  const key = taskKey(task);
  const next = new Set(expanded.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expanded.value = next;
}

function isExpanded(task: PlanTask): boolean {
  return expanded.value.has(taskKey(task));
}

const counts = computed(() => ({
  mine: taskPool.value.filter((task) => task.isMine).length,
  related: taskPool.value.filter((task) => task.isRelated).length,
  all: taskPool.value.length,
}));

function relationLabel(task: PlanTask): string | null {
  if (task.relationKinds?.includes('owner')) return '我负责';
  if (task.relationKinds?.includes('reviewer')) return '待我评审';
  if (task.relationKinds?.includes('deliverable-owner')) return '我负责交付物';
  return null;
}

/**
 * 交付物提交进审批流、还没人批 → 这个任务的状态就是「待评审」（0820 第 6 条）。
 * 也逾期了不能只说「待评审」——活是交了，但期限已经过了，两件事都得说。
 */
function isAwaitingReview(task: PlanTask): boolean {
  return task.approvalStatus === 'pending' && !isDone(task);
}

/** 还没动过：没实际完成、进度 0/空、状态还是待处理 */
function notStarted(task: PlanTask): boolean {
  return !task.actualEnd
    && !task.progress
    && ['pending', 'open', '', 'not_started'].includes(task.status ?? '');
}

/** 状态标签和颜色必须同源，否则会出现文字「待评审」配红三角这种自相矛盾 */
function chipValue(task: PlanTask): string {
  if (isDone(task)) return 'completed';
  if (isAwaitingReview(task)) return task.overdue ? 'overdue' : 'in_review';
  if (task.overdue) return 'overdue';
  return task.status;
}

function taskStatusLabel(task: PlanTask): string {
  if (isLateDone(task)) return '超期完成';
  if (isDone(task)) return '已完成';
  if (isAwaitingReview(task)) return task.overdue ? '待评审（已超期）' : '待评审';
  if (task.overdue) return '已逾期';
  if (notStarted(task)) return '未开始';
  // 状态还是 pending 但进度已经有了：人在干，只是没人去改状态
  if (['pending', 'open'].includes(task.status ?? '') && task.progress) return '进行中';
  const labels: Record<string, string> = {
    pending: '待处理',
    open: '待处理',
    in_progress: '进行中',
    blocked: '受阻',
    completed: '已完成',
    approved: '已通过',
    cancelled: '已取消',
    not_applicable: '不适用',
  };
  return labels[task.status] || task.status || '未定义';
}

function stageNameFor(task: PlanTask): string {
  const key = stageKeyFor(task);
  return phaseRoots.value.find((phase) => taskKey(phase) === key)?.name || '未归入阶段';
}

const exportRows = computed(() => filteredTasks.value.map((task) =>
  planTaskExportRow(props.project, task, stageNameFor(task), taskStatusLabel(task))));
const exportFilename = computed(() =>
  buildExportFilename([props.project.projectNo, '任务看板']));

function planLocation(task: PlanTask, edit = false) {
  return {
    name: edit ? 'project-activities' : 'project-plan',
    params: { projectId: String(props.project.id) },
    query: { nodeId: String(task.id), ...(edit ? { edit: '1' } : {}) },
  };
}

function deliverableLocation(task: PlanTask) {
  return {
    name: 'project-deliverables',
    params: { projectId: String(props.project.id) },
    query: { nodeId: String(task.id), taskName: task.name },
  };
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.projectPlan(props.project.id);
    if (!payload || !Array.isArray(payload.tasks)) throw new Error('项目任务看板响应缺少 tasks 数组');
    data.value = payload;
    // 名下没有任务时不要甩一块白板：项目有 184 条任务，工程师却什么都看不到。
    // 旧 EQS 默认就是项目全貌，「只看我的」是个可选开关。
    if (!counts.value.mine) {
      scope.value = counts.value.related ? 'related' : 'all';
      autoWidened.value = true;
    }
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<template>
  <header class="subpage-context">
    <p><span>TASK BOARD</span>按 P01–P06 阶段查看任务，先定位自己的工作，再进入计划或交付物处理。</p>
    <small v-if="data">数据截至 {{ formatDate(data.asOf) }}</small>
  </header>
  <PageState v-if="loading" state="loading" title="正在读取任务执行看板" message="加载阶段、任务、负责人和日期。" />
  <PageState v-else-if="error" state="error" title="任务执行看板不可用" message="不会用演示任务替代生产数据。" :detail="error" @retry="load" />
  <template v-else-if="data">
    <section class="board-controls" aria-label="任务看板筛选">
      <div class="scope-switch" role="group" aria-label="任务范围">
        <button type="button" :class="{ active: scope === 'mine' }" :aria-pressed="scope === 'mine'" @click="scope = 'mine'">
          <span>我的任务 <strong>{{ counts.mine }}</strong></span><small>点击筛选</small>
        </button>
        <button type="button" :class="{ active: scope === 'related' }" :aria-pressed="scope === 'related'" @click="scope = 'related'">
          <span>与我相关 <strong>{{ counts.related }}</strong></span><small>点击筛选</small>
        </button>
        <button type="button" :class="{ active: scope === 'all' }" :aria-pressed="scope === 'all'" @click="scope = 'all'">
          <span>项目全部 <strong>{{ counts.all }}</strong></span><small>点击筛选</small>
        </button>
      </div>
      <label>
        <span>任务状态</span>
        <select v-model="statusFilter">
          <option value="open">未完成</option>
          <option value="all">全部状态</option>
          <option value="overdue">已逾期</option>
          <option value="blocked">受阻</option>
          <option value="completed">已完成 / 不适用</option>
          <option value="unscheduled">未排期</option>
        </select>
      </label>
      <label class="board-search">
        <span>快速查找</span>
        <input v-model="query" type="search" placeholder="任务、负责人、交付物">
      </label>
      <div class="board-result-actions">
        <strong class="result-count">{{ filteredTasks.length }} 项</strong>
        <ExportDataButton
          :columns="planExportColumns"
          :rows="exportRows"
          :filename="exportFilename"
          label="导出当前任务"
          compact
        />
      </div>
    </section>

    <p v-if="autoWidened && scope !== 'mine'" class="board-widened">
      你在本项目名下没有任务，已自动切到「{{ scope === 'related' ? '与我相关' : '项目全部' }}」。
    </p>

    <section v-if="filteredTasks.length" class="task-board" aria-label="P01 至 P06 任务执行看板">
      <article v-for="column in columns" :key="column.key" class="stage-column">
        <header>
          <div>
            <b>{{ column.code }}</b>
            <span>{{ column.name }}</span>
            <!-- 旧看板最有价值的一格：一眼看出这个阶段推到哪了。
                 分母固定用阶段全部任务，不跟着筛选变，否则两次看到的百分比没法比。 -->
            <small>{{ column.done }} / {{ column.total }}（{{ column.percent }}%）</small>
          </div>
          <strong :title="`当前筛选下 ${column.rows.length} 行`">{{ column.rows.length }}</strong>
        </header>
        <div class="stage-progress" role="presentation">
          <i :style="{ width: `${column.percent}%` }"></i>
        </div>
        <div class="stage-cards">
          <template v-for="row in column.rows" :key="row.task.id">
            <section
              class="task-card"
              :class="{
                overdue: row.task.overdue && !isDone(row.task),
                blocked: row.task.status === 'blocked',
                complete: isDone(row.task) && !isLateDone(row.task),
                'late-done': isLateDone(row.task),
                'matched-by-child': row.matchedByChild,
              }"
            >
              <div class="card-topline">
                <!-- 旧看板的 ►：有子任务的行折起来，不占满整列 -->
                <button
                  v-if="row.children.length"
                  type="button"
                  class="row-toggle"
                  :aria-expanded="isExpanded(row.task)"
                  :aria-label="`${isExpanded(row.task) ? '收起' : '展开'}“${row.task.name}”的 ${row.children.length} 个子任务`"
                  @click="toggleRow(row.task)"
                >{{ isExpanded(row.task) ? '▾' : '▸' }} {{ row.children.length }}</button>
                <StatusChip
                  :value="chipValue(row.task)"
                  :label="taskStatusLabel(row.task)"
                />
                <span v-if="relationLabel(row.task)" class="relation-badge">{{ relationLabel(row.task) }}</span>
              </div>
              <RouterLink
                class="task-main-link"
                :to="planLocation(row.task)"
                :aria-label="`打开任务“${row.task.name}”并在甘特图中定位`"
              >
                <b>{{ row.task.name }}</b>
                <span>打开甘特图定位 →</span>
              </RouterLink>
              <small v-if="row.matchedByChild" class="matched-note">本行未命中筛选，因子任务命中而显示</small>
              <dl>
                <div><dt>负责人</dt><dd>{{ formatResponsibility(row.task.ownerName || row.task.ownerRole, '未分配') }}</dd></div>
                <div><dt>计划</dt><dd>{{ formatDate(row.task.planStart) }} → {{ formatDate(row.task.planEnd) }}</dd></div>
                <div v-if="row.task.outputs?.length"><dt>交付物</dt><dd>{{ row.task.outputs.slice(0, 2).join('；') }}</dd></div>
              </dl>
              <!-- 两个次级操作平时收起：P03 有 67 张卡，每张都常驻三行链接就成了一堵墙。
                   用 focus-within 保证键盘用户一样能到达。 -->
              <footer>
                <RouterLink :to="planLocation(row.task, true)">编辑内容 →</RouterLink>
                <RouterLink :to="deliverableLocation(row.task)">上传交付物 →</RouterLink>
              </footer>
            </section>

            <section
              v-for="child in (isExpanded(row.task) ? row.children : [])"
              :key="`${row.task.id}-${child.task.id}`"
              class="task-card task-child"
              :style="{ marginLeft: `${child.depth * 12}px` }"
              :class="{
                overdue: child.task.overdue && !isDone(child.task),
                blocked: child.task.status === 'blocked',
                complete: isDone(child.task) && !isLateDone(child.task),
                'late-done': isLateDone(child.task),
              }"
            >
              <div class="card-topline">
                <StatusChip
                  :value="chipValue(child.task)"
                  :label="taskStatusLabel(child.task)"
                />
                <span v-if="relationLabel(child.task)" class="relation-badge">{{ relationLabel(child.task) }}</span>
              </div>
              <RouterLink
                class="task-main-link"
                :to="planLocation(child.task)"
                :aria-label="`打开子任务“${child.task.name}”并在甘特图中定位`"
              >
                <b>{{ child.task.name }}</b>
                <span>打开甘特图定位 →</span>
              </RouterLink>
              <dl>
                <div><dt>负责人</dt><dd>{{ formatResponsibility(child.task.ownerName || child.task.ownerRole, '未分配') }}</dd></div>
                <div><dt>计划</dt><dd>{{ formatDate(child.task.planStart) }} → {{ formatDate(child.task.planEnd) }}</dd></div>
              </dl>
              <footer>
                <RouterLink :to="planLocation(child.task, true)">编辑内容 →</RouterLink>
                <RouterLink :to="deliverableLocation(child.task)">上传交付物 →</RouterLink>
              </footer>
            </section>
          </template>
          <p v-if="!column.rows.length" class="stage-empty">当前筛选下无任务</p>
        </div>
      </article>
    </section>
    <section v-else class="board-empty">
      <b>{{ scope === 'mine' ? '当前账号没有符合条件的本人任务' : '当前筛选没有任务' }}</b>
      <span v-if="scope === 'mine'">本人任务只按明确的负责人账号识别，不使用姓名模糊匹配。</span>
      <button v-if="scope !== 'all'" type="button" @click="scope = 'all'; statusFilter = 'open'">查看项目全部未完成任务</button>
    </section>
  </template>
</template>

<style scoped>
.board-controls {
  display: grid;
  grid-template-columns: minmax(360px, 1.4fr) 150px minmax(210px, .8fr) auto;
  gap: 10px;
  align-items: end;
  margin: 16px 0 12px;
  padding: 12px;
  border: 1px solid var(--line);
  background: var(--white);
}

.board-result-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.scope-switch {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 4px;
  background: var(--surface);
}

.scope-switch button {
  display: grid;
  align-items: center;
  justify-items: center;
  gap: 2px;
  min-height: 52px;
  border: 1px solid transparent;
  color: var(--muted);
  background: transparent;
  cursor: pointer;
  font-weight: 700;
}

.scope-switch button:hover,
.scope-switch button:focus-visible {
  border-color: var(--blue);
  color: var(--blue);
  outline: none;
  background: white;
}
.scope-switch button.active { color: var(--white); background: var(--blue); }
.scope-switch button span { display: flex; align-items: center; gap: 8px; }
.scope-switch button small { color: var(--muted); font-size: 9px; font-weight: 600; }
.scope-switch button.active small { color: rgba(255,255,255,.78); }
.scope-switch strong { color: var(--blue); }
.board-controls label { display: grid; gap: 5px; }
.board-controls label > span { color: var(--muted); font-size: 12px; font-weight: 700; }
.board-controls select,
.board-controls input {
  min-height: 42px;
  border: 1px solid var(--line);
  background: white;
}
.result-count { padding: 0 4px 12px; color: var(--blue); white-space: nowrap; }

/* 六列硬排 270px 起，在 1280 屏上必然横向滚动——看个项目全貌要来回拖。
   改成自动折行：宽屏一行放下六列，窄屏折成两行，纵向滚动本来就顺手。 */
.task-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 10px;
  padding: 0 0 10px;
}

.stage-column {
  min-width: 0;
  border: 1px solid var(--line);
  background: var(--surface);
}

.stage-column > header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 58px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--line);
  color: var(--ink);
  background: var(--paper-2);
}

.stage-column > header div { display: grid; min-width: 0; }
.stage-column > header b { color: var(--blue); font: 800 13px var(--display); }
.stage-column > header span { overflow: hidden; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.stage-column > header strong {
  display: grid;
  place-items: center;
  min-width: 30px;
  height: 30px;
  border-radius: 50%;
  color: var(--white);
  background: var(--blue);
}

.stage-cards {
  display: grid;
  align-content: start;
  gap: 8px;
  max-height: 62vh;
  overflow-y: auto;
  padding: 8px;
}

.stage-progress {
  height: 4px;
  background: var(--line);
}

.stage-progress i {
  display: block;
  height: 100%;
  background: var(--blue);
  transition: width .2s ease;
}

.stage-column > header small {
  color: var(--muted);
  font-size: 12px;
}

.board-widened {
  margin: 0 0 10px;
  padding: 8px 11px;
  border-left: 3px solid var(--blue);
  background: #EEF4FA;
  color: var(--ink);
  font-size: 12px;
}

.row-toggle {
  flex: 0 0 auto;
  padding: 1px 6px;
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--blue);
  font: 700 11px var(--display);
  cursor: pointer;
}

.row-toggle:hover,
.row-toggle:focus-visible { border-color: var(--blue); background: #EEF4FA; }

/* 子任务比父行窄一档，缩进 + 左侧细线，一眼看出层级 */
.task-child {
  border-left-width: 2px;
  box-shadow: none;
  opacity: .94;
}

.task-child .task-main-link b { font-size: 12px; }

.task-card.matched-by-child { border-style: dashed; }

.matched-note {
  color: var(--muted);
  font-size: 12px;
}

.task-card {
  display: grid;
  gap: 9px;
  padding: 12px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--blue);
  background: white;
  box-shadow: 0 2px 8px rgba(26, 45, 61, .04);
}
/* --red / --amber 这两个变量从来就不存在，逾期卡的红色边框一直没生效，
   看板最有用的「一眼分状态」等于是坏的。正确的变量是 --risk / --warning-graphic。 */
.task-card.overdue { border-left-color: var(--risk); background: #FDF6F5; }
.task-card.blocked { border-left-color: var(--warning-graphic); background: #FFFBF2; }
.task-card.complete { border-left-color: var(--green); background: #F5FAF8; opacity: .82; }
/* 超期完成：做完了但晚了，旧看板单独用橙色标——和「已逾期未做」不是一回事 */
.task-card.late-done { border-left-color: var(--warning-graphic); background: #FFFBF2; opacity: .9; }
.card-topline { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.relation-badge { color: var(--blue); font-size: 12px; font-weight: 800; }
.task-main-link {
  display: grid;
  gap: 7px;
  padding: 10px;
  border: 1px solid rgba(18, 109, 166, .22);
  color: var(--ink);
  background: rgba(18, 109, 166, .045);
  line-height: 1.35;
  text-decoration: none;
}
.task-main-link b { font-size: 14px; }
.task-main-link span {
  color: var(--blue);
  font-size: 10px;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.task-main-link:hover,
.task-main-link:focus-visible {
  border-color: var(--blue);
  outline: 2px solid var(--blue);
  outline-offset: 1px;
  background: rgba(18, 109, 166, .09);
}
.task-card dl { display: grid; gap: 4px; margin: 0; }
.task-card dl div { display: grid; grid-template-columns: 46px minmax(0, 1fr); gap: 5px; }
.task-card dt { color: var(--muted); font-size: 10px; }
.task-card dd { overflow: hidden; margin: 0; color: var(--ink); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
/* 平时只留 状态 / 任务名 / 责任人 / 日期 四行，和旧看板一样紧凑。
   P03 有 67 张卡，每张常驻两行操作链接就成了一堵墙。
   focus-within 保证键盘用户一样能到达。 */
.task-card footer { display: none; gap: 6px; padding-top: 3px; border-top: 1px solid var(--line); }
.task-card:hover footer,
.task-card:focus-within footer { display: flex; }
.task-card footer a {
  flex: 1;
  padding: 7px 5px;
  border: 1px solid var(--line);
  color: var(--blue);
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  text-decoration: none;
}
.task-card footer a:hover,
.task-card footer a:focus-visible {
  border-color: var(--blue);
  outline: none;
  background: rgba(18, 109, 166, .08);
}
.stage-empty { margin: 18px 8px; color: var(--muted); font-size: 12px; text-align: center; }

.board-empty {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 60px 20px;
  border: 1px dashed var(--line-strong);
  color: var(--muted);
  background: var(--surface);
  text-align: center;
}
.board-empty b { color: var(--ink); font-size: 16px; }
.board-empty button { padding: 9px 14px; border: 0; color: white; background: var(--blue); cursor: pointer; }

@media (max-width: 900px) {
  .board-controls { grid-template-columns: 1fr 1fr; }
  .scope-switch { grid-column: 1 / -1; }
}

@media (max-width: 600px) {
  .board-controls { grid-template-columns: 1fr; }
  .scope-switch { grid-template-columns: 1fr; }
  .task-board { grid-template-columns: repeat(6, minmax(86vw, 1fr)); }
}
</style>
